import React, { useEffect, useMemo, useState } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Typography,
  Row,
  Col,
  Card
} from 'antd';
import { 
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/Layouts/AdminLayout/AdminLayout';
import { message } from 'antd';
import { adminWordExamApi } from '@/services/api';

const { Title } = Typography;
const { Option } = Select;

interface WordExamData {
  id: number;
  name: string;
  type: string; // 'word' | 'meaning'
  registrant?: string;
  registrationDate?: string;
}

const AdminWordExamList: React.FC = () => {
  const navigate = useNavigate();
  const [searchName, setSearchName] = useState<string>('');
  const [searchType, setSearchType] = useState<string>('');
  const [searchRegistrant, setSearchRegistrant] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<WordExamData[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const typeOptions = [
    { value: '단어 작성형', label: '단어 작성형' },
    { value: '해석 작성형', label: '해석 작성형' },
  ];

  const registrantOptions = [
    { value: '김관리자', label: '김관리자' },
    { value: '이관리자', label: '이관리자' },
    { value: '박관리자', label: '박관리자' }
  ];

  const loadList = async () => {
    setLoading(true);
    try {
      const typeParam = searchType === '단어 작성형' ? 'word' : (searchType === '해석 작성형' ? 'meaning' : '');
      const res = await adminWordExamApi.list({ 
        q: searchName.trim() || '', 
        type: typeParam, 
        registrant: searchRegistrant.trim() || '', 
        page: page - 1, 
        size: pageSize 
      });
      const data = res?.data || res;
      const content = data?.content || data?.data?.content || [];
      const totalElements = data?.totalElements || data?.data?.totalElements || 0;
      setItems(content as WordExamData[]);
      setTotal(totalElements);
    } catch (e) {
      message.error('단어시험 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchName, searchType, searchRegistrant, page, pageSize]);

  const handleAddWordExam = () => {
    navigate('/admin/word-exam/add');
  };

  const handleEditWordExam = (id: number) => {
    navigate(`/admin/word-exam/detail/${id}`);
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  const mappedItems = useMemo(() => items.map(it => ({
    ...it,
    // 서버 type(word|meaning) → 한글 라벨 표시
    typeLabel: it.type === 'word' ? '단어 작성형' : it.type === 'meaning' ? '해석 작성형' : it.type,
    formattedDate: formatDateTime(it.registrationDate)
  })), [items]);

  const columns = [
    {
      title: '순번',
      dataIndex: 'id',
      key: 'id',
      render: (_: any, __: any, index: number) => total - ((page - 1) * pageSize + index),
      width: 80,
      align: 'center' as const
    },
    {
      title: '단어시험명',
      dataIndex: 'name',
      key: 'name',
      width: 200
    },
    {
      title: '시험유형',
      dataIndex: 'typeLabel',
      key: 'type',
      width: 120,
      align: 'center' as const
    },
    {
      title: '등록자',
      dataIndex: 'registrant',
      key: 'registrant',
      width: 120
    },
    {
      title: '등록일',
      dataIndex: 'formattedDate',
      key: 'registrationDate',
      width: 150,
      align: 'center' as const
    },

  ];

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>단어시험 관리</Title>
        </div>

        {/* 검색 영역 */}
          <Row gutter={16} align="bottom" className='mb-4'>
            <Col span={6}>
              <label className="block mb-2 text-sm font-medium">단어시험명</label>
              <Input
                placeholder="단어시험명을 입력하세요"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                prefix={<SearchOutlined />}
                size="large"
              />
            </Col>
            <Col span={4}>
              <label className="block mb-2 text-sm font-medium">시험유형</label>
              <Select
                placeholder="전체"
                value={searchType || undefined}
                onChange={setSearchType}
                allowClear
                style={{ width: '100%' }}
                size="large"
              >
                {typeOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <label className="block mb-2 text-sm font-medium">등록자</label>
              <Input
                placeholder="등록자명을 입력하세요"
                value={searchRegistrant}
                onChange={(e) => setSearchRegistrant(e.target.value)}
                size="large"
              />
            </Col>
            <Col span={10} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddWordExam}
                size="large"
              >
                단어시험 등록
              </Button>
            </Col>
          </Row>
        

        {/* 테이블 */}
        
          <Table
            columns={columns}
            dataSource={mappedItems}
            loading={loading}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleEditWordExam(record.id),
              style: { cursor: 'pointer' }
            })}
            pagination={{
              total: total,
              current: page,
              pageSize: pageSize,
              showSizeChanger: true,
              onChange: (p, ps) => { setPage(p); setPageSize(ps); },
              showTotal: (t, range) => `${range[0]}-${range[1]} / 총 ${t}개`
            }}
          />
      </div>
    </AdminLayout>
  );
};

export default AdminWordExamList; 